import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  YAMLFormatter, Base64Tool, URLTool, JWTDecoder, UUIDGenerator, HashGenerator,
  HMACGenerator, RegexTester
} from '../components/DevToolsExtension';
import { useNavigate } from 'react-router-dom';

import SEO from '../components/SEO';

/* ─── DESIGN TOKENS ─────────────────────────────────────────────────── */
const T = {
  bg:      '#0a0c10',
  bg2:     '#0e1117',
  bg3:     '#13171f',
  surface: '#1a1f2b',
  border:  'rgba(255,255,255,.07)',
  borderHi:'rgba(255,255,255,.12)',
  white:   '#e8edf5',
  muted:   'rgba(255,255,255,0.50)',
  dim:     'rgba(255,255,255,0.50)',
  green:   '#3dd68c',
  red:     '#ff5f57',
  yellow:  '#fbbf24',
  blue:    '#4d9fff',
  purple:  '#b78cff',
  orange:  '#ff9f43',
  cyan:    '#00d4ff',
  mono:    '"JetBrains Mono","Fira Code","Cascadia Code",monospace',
  sans:    '"DM Sans","Outfit","system-ui",sans-serif',
};

/* ─── SHARED ATOMS ───────────────────────────────────────────────────── */
function Pill({ children, active, onClick, color = T.blue }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 16px',
      borderRadius: 6,
      border: `1px solid ${active ? color + '60' : T.border}`,
      background: active ? color + '18' : 'transparent',
      color: active ? color : T.muted,
      fontSize: 12.5,
      fontWeight: 600,
      fontFamily: T.sans,
      cursor: 'pointer',
      letterSpacing: '.03em',
      transition: 'all .15s',
    }}>{children}</button>
  );
}

function StatusBadge({ ok, label }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px',
      borderRadius: 5,
      background: ok ? T.green + '18' : T.red + '18',
      border: `1px solid ${ok ? T.green + '40' : T.red + '40'}`,
      fontSize: 11.5,
      fontWeight: 700,
      color: ok ? T.green : T.red,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      fontFamily: T.sans,
    }}>
      <span style={{ fontSize: 9 }}>{ok ? '●' : '●'}</span>
      {label}
    </div>
  );
}

function IconBtn({ title, onClick, children, danger }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '5px 10px',
        borderRadius: 5,
        border: `1px solid ${hover ? (danger ? T.red + '50' : T.borderHi) : T.border}`,
        background: hover ? (danger ? T.red + '12' : T.surface) : 'transparent',
        color: hover ? (danger ? T.red : T.white) : T.muted,
        fontSize: 11.5,
        fontFamily: T.sans,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all .12s',
        display: 'flex', alignItems: 'center', gap: 5,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: T.dim,
      fontFamily: T.sans,
      marginBottom: 6,
    }}>{children}</div>
  );
}

const EDITOR_STYLE = {
  width: '100%',
  minHeight: 320,
  padding: '14px 16px',
  background: T.bg,
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  color: T.white,
  fontSize: 12.5,
  fontFamily: T.mono,
  lineHeight: 1.65,
  resize: 'vertical',
  outline: 'none',
  tabSize: 2,
  transition: 'border-color .15s',
};

/* ─── JSON SYNTAX HIGHLIGHTER ────────────────────────────────────────── */
function highlight(json) {
  return json
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, match => {
      let cls = 'number';
      if (/^"/.test(match)) cls = /:$/.test(match) ? 'key' : 'string';
      else if (/true|false/.test(match)) cls = 'bool';
      else if (/null/.test(match)) cls = 'null';
      const colors = {
        key:    T.blue,
        string: T.green,
        number: T.orange,
        bool:   T.purple,
        null:   T.red,
      };
      return `<span style="color:${colors[cls]}">${match}</span>`;
    });
}

/* ─── 1. JSON FORMATTER ──────────────────────────────────────────────── */
function JSONTool() {
  const [input,    setInput]    = useState('');
  const [output,   setOutput]   = useState('');
  const [status,   setStatus]   = useState(null);   // null | {ok, msg}
  const [indent,   setIndent]   = useState(2);
  const [mode,     setMode]     = useState('format'); // format | minify | validate
  const [copied,   setCopied]   = useState(false);
  const [stats,    setStats]    = useState(null);
  const inputRef = useRef(null);

  const SAMPLE = `{
  "name": "Tylented",
  "type": "AI Engineering Agency",
  "services": ["Django", "FastAPI", "OpenAI", "AWS"],
  "founded": 2021,
  "active": true,
  "contact": {
    "email": "hello@arhatinfo.com",
    "website": "arhatinfo.com"
  }
}`;

  const process = useCallback(() => {
    const raw = input.trim();
    if (!raw) { setStatus(null); setOutput(''); setStats(null); return; }

    try {
      const parsed = JSON.parse(raw);

      if (mode === 'validate') {
        setStatus({ ok: true, msg: 'Valid JSON ✓' });
        setOutput(JSON.stringify(parsed, null, indent));
        countStats(parsed);
        return;
      }
      if (mode === 'minify') {
        const min = JSON.stringify(parsed);
        setOutput(min);
        setStatus({ ok: true, msg: `Minified — ${raw.length} → ${min.length} chars` });
        countStats(parsed);
        return;
      }
      // format
      const formatted = JSON.stringify(parsed, null, indent);
      setOutput(formatted);
      setStatus({ ok: true, msg: `Valid JSON — ${formatted.split('\n').length} lines` });
      countStats(parsed);

    } catch (e) {
      setStatus({ ok: false, msg: e.message });
      setOutput('');
      setStats(null);
    }
  }, [input, mode, indent]);

  const countStats = (obj) => {
    let keys = 0, arrays = 0, depth = 0;
    const walk = (v, d = 0) => {
      if (d > depth) depth = d;
      if (Array.isArray(v)) { arrays++; v.forEach(x => walk(x, d+1)); }
      else if (v && typeof v === 'object') { Object.values(v).forEach(x => { keys++; walk(x, d+1); }); }
    };
    walk(obj);
    setStats({ keys, arrays, depth });
  };

  useEffect(() => { if (input) process(); }, [mode, indent]);

  const copy = () => {
    navigator.clipboard?.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const loadSample = () => { setInput(SAMPLE); setTimeout(process, 50); };

  // Tab support in textarea
  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const el  = e.target;
      const s   = el.selectionStart;
      const end = el.selectionEnd;
      const val = el.value;
      const spaces = ' '.repeat(indent);
      setInput(val.substring(0, s) + spaces + val.substring(end));
      setTimeout(() => { el.selectionStart = el.selectionEnd = s + indent; }, 0);
    }
  };

  return (
    <div>
      {/* toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:4 }}>
          {['format','minify','validate'].map(m => (
            <Pill key={m} active={mode===m} onClick={()=>setMode(m)}>
              {m.charAt(0).toUpperCase()+m.slice(1)}
            </Pill>
          ))}
        </div>
        <div style={{ display:'flex', gap:4, marginLeft:'auto', alignItems:'center' }}>
          <span style={{ fontSize:11.5, color:T.muted, marginRight:2 }}>Indent</span>
          {[2,4].map(n => (
            <Pill key={n} active={indent===n} onClick={()=>setIndent(n)} color={T.cyan}>
              {n}
            </Pill>
          ))}
        </div>
      </div>

      {/* editors */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <Label>Input</Label>
            <div style={{ display:'flex', gap:5 }}>
              <IconBtn onClick={loadSample}>Sample</IconBtn>
              <IconBtn onClick={()=>{ setInput(''); setOutput(''); setStatus(null); setStats(null); }} danger>
                Clear
              </IconBtn>
            </div>
          </div>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={process}
            placeholder={`Paste JSON here…\n\n{\n  "key": "value"\n}`}
            spellCheck={false}
            style={{
              ...EDITOR_STYLE,
              borderColor: status ? (status.ok ? T.green+'40' : T.red+'50') : T.border,
            }}
          />
          {/* status */}
          <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            {status && <StatusBadge ok={status.ok} label={status.ok ? 'Valid' : 'Invalid'}/>}
            {status && (
              <span style={{ fontSize:11.5, color: status.ok ? T.muted : T.red, fontFamily:T.mono }}>
                {status.msg}
              </span>
            )}
          </div>
          {/* stats */}
          {stats && (
            <div style={{
              marginTop:8, display:'flex', gap:10, flexWrap:'wrap',
            }}>
              {[
                ['Keys',   stats.keys],
                ['Arrays', stats.arrays],
                ['Depth',  stats.depth],
                ['Chars',  input.length],
              ].map(([label, val]) => (
                <div key={label} style={{
                  padding:'3px 9px',
                  background:T.surface,
                  border:`1px solid ${T.border}`,
                  borderRadius:5,
                  fontSize:11,
                  color:T.muted,
                  fontFamily:T.mono,
                }}>
                  <span style={{ color:T.dim }}>{label} </span>
                  <span style={{ color:T.cyan }}>{val}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <Label>Output</Label>
            {output && (
              <IconBtn onClick={copy}>
                {copied ? '✓ Copied' : '⧉ Copy'}
              </IconBtn>
            )}
          </div>
          <div style={{
            ...EDITOR_STYLE,
            minHeight: 320,
            overflow: 'auto',
            whiteSpace: 'pre',
            cursor: 'text',
            userSelect: 'text',
            borderColor: output ? T.green+'30' : T.border,
          }}>
            {output
              ? <div dangerouslySetInnerHTML={{ __html: highlight(output) }}/>
              : <span style={{ color:T.dim }}>Formatted output will appear here…</span>
            }
          </div>
          {output && mode === 'format' && (
            <div style={{ marginTop:8, display:'flex', gap:5 }}>
              <IconBtn onClick={() => setInput(output)}>
                ↩ Use as input
              </IconBtn>
            </div>
          )}
        </div>
      </div>

      {/* keyboard hint */}
      <div style={{ marginTop:10, fontSize:11, color:T.dim }}>
        Tip: JSON is validated in real-time as you type · Tab key inserts spaces
      </div>
    </div>
  );
}

/* ─── 2. TEXT DIFF ───────────────────────────────────────────────────── */

function computeDiff(a, b) {
  const linesA = a.split('\n');
  const linesB = b.split('\n');

  // Myers diff algorithm (simplified LCS-based)
  const n = linesA.length;
  const m = linesB.length;

  // LCS DP
  const dp = Array.from({ length: n+1 }, () => new Array(m+1).fill(0));
  for (let i = 1; i <= n; i++)
    for (let j = 1; j <= m; j++)
      dp[i][j] = linesA[i-1] === linesB[j-1]
        ? dp[i-1][j-1] + 1
        : Math.max(dp[i-1][j], dp[i][j-1]);

  // Backtrack
  const result = [];
  let i = n, j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && linesA[i-1] === linesB[j-1]) {
      result.unshift({ type:'same',    text:linesA[i-1], lineA:i, lineB:j });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
      result.unshift({ type:'added',   text:linesB[j-1], lineB:j });
      j--;
    } else {
      result.unshift({ type:'removed', text:linesA[i-1], lineA:i });
      i--;
    }
  }
  return result;
}

function inlineDiff(a, b) {
  // Character-level diff for changed lines
  const wordsA = a.split(/(\s+)/);
  const wordsB = b.split(/(\s+)/);
  const n = wordsA.length, m = wordsB.length;
  const dp = Array.from({ length:n+1 }, () => new Array(m+1).fill(0));
  for (let i=1;i<=n;i++)
    for (let j=1;j<=m;j++)
      dp[i][j] = wordsA[i-1]===wordsB[j-1] ? dp[i-1][j-1]+1 : Math.max(dp[i-1][j],dp[i][j-1]);
  const res = [];
  let i=n,j=m;
  while(i>0||j>0){
    if(i>0&&j>0&&wordsA[i-1]===wordsB[j-1]){ res.unshift({t:'same',v:wordsA[i-1]}); i--;j--; }
    else if(j>0&&(i===0||dp[i][j-1]>=dp[i-1][j])){ res.unshift({t:'add',v:wordsB[j-1]}); j--; }
    else { res.unshift({t:'del',v:wordsA[i-1]}); i--; }
  }
  return res;
}

function DiffLine({ item, showInline, lineNumWidth }) {
  const colors = {
    same:    { bg:'transparent', border:'transparent', num: T.dim, text:T.muted },
    added:   { bg:'rgba(61,214,140,.07)', border:T.green+'40', num:T.green+'80', text:T.green },
    removed: { bg:'rgba(255,95,87,.07)',  border:T.red+'40',   num:T.red+'80',   text:T.red  },
  };
  const c   = colors[item.type];
  const sym = item.type==='added' ? '+' : item.type==='removed' ? '−' : ' ';

  return (
    <div style={{
      display:'flex',
      background:c.bg,
      borderLeft:`2px solid ${c.border}`,
      minHeight:22,
    }}>
      {/* line numbers */}
      <div style={{
        display:'flex', flexShrink:0,
        borderRight:`1px solid ${T.border}`,
        userSelect:'none',
      }}>
        <div style={{
          width:lineNumWidth, textAlign:'right',
          padding:'1px 8px', fontSize:11,
          fontFamily:T.mono, color:c.num,
        }}>
          {item.type!=='added' ? (item.lineA||'') : ''}
        </div>
        <div style={{
          width:lineNumWidth, textAlign:'right',
          padding:'1px 8px', fontSize:11,
          fontFamily:T.mono, color:c.num,
          borderLeft:`1px solid ${T.border}`,
        }}>
          {item.type!=='removed' ? (item.lineB||'') : ''}
        </div>
      </div>
      {/* symbol */}
      <div style={{
        width:22, textAlign:'center', flexShrink:0,
        fontSize:12, fontFamily:T.mono, color:c.text,
        padding:'1px 0',
      }}>{sym}</div>
      {/* content */}
      <div style={{
        flex:1, padding:'1px 6px',
        fontSize:12.5, fontFamily:T.mono,
        color: item.type==='same' ? T.muted : c.text,
        whiteSpace:'pre-wrap', wordBreak:'break-all',
      }}>
        {showInline && item.type!=='same' && item._pair
          ? item._inlineSegs?.map((seg,i) => (
              <span key={i} style={{
                background: seg.t==='add'
                  ? T.green+'30'
                  : seg.t==='del'
                  ? T.red+'30'
                  : 'transparent',
                borderRadius:2,
              }}>{seg.v}</span>
            ))
          : item.text
        }
      </div>
    </div>
  );
}

function DiffTool() {
  const [left,    setLeft]    = useState('');
  const [right,   setRight]   = useState('');
  const [diff,    setDiff]    = useState(null);
  const [inline,  setInline]  = useState(true);
  const [context, setContext] = useState(3);   // lines of context around changes
  const [stats,   setStats]   = useState(null);

  const SAMPLE_A = `function greet(name) {
  console.log("Hello, " + name);
  return name;
}

const users = ["Alice", "Bob"];
users.forEach(greet);
`;

  const SAMPLE_B = `function greet(name, greeting = "Hello") {
  console.log(greeting + ", " + name + "!");
  return { name, greeting };
}

const users = ["Alice", "Bob", "Charlie"];
users.forEach(u => greet(u));
`;

  const runDiff = useCallback(() => {
    if (!left && !right) { setDiff(null); setStats(null); return; }
    const raw = computeDiff(left, right);

    // Pair removed/added for inline diff
    const paired = [];
    let i = 0;
    while (i < raw.length) {
      if (raw[i].type==='removed' && raw[i+1]?.type==='added') {
        const segsR = inlineDiff(raw[i].text, raw[i+1].text).map(s =>
          s.t==='add' ? { ...s, t:'same' } : s
        );
        const segsA = inlineDiff(raw[i].text, raw[i+1].text).map(s =>
          s.t==='del' ? { ...s, t:'same' } : s
        );
        paired.push({ ...raw[i],   _pair:true, _inlineSegs:segsR });
        paired.push({ ...raw[i+1], _pair:true, _inlineSegs:segsA });
        i += 2;
      } else {
        paired.push(raw[i]);
        i++;
      }
    }

    // Compute stats
    const added   = paired.filter(l=>l.type==='added').length;
    const removed = paired.filter(l=>l.type==='removed').length;
    const same    = paired.filter(l=>l.type==='same').length;
    setStats({ added, removed, same, total: paired.length });

    // Apply context windowing — hide same lines far from changes
    if (context >= 0) {
      const changed = new Set();
      paired.forEach((l, idx) => { if (l.type!=='same') changed.add(idx); });
      const visible = new Set();
      changed.forEach(idx => {
        for (let k=Math.max(0,idx-context); k<=Math.min(paired.length-1,idx+context); k++)
          visible.add(k);
      });
      const windowed = [];
      let   prevHidden = false;
      paired.forEach((l, idx) => {
        if (visible.has(idx)) {
          if (prevHidden) windowed.push({ type:'ellipsis' });
          windowed.push(l);
          prevHidden = false;
        } else {
          prevHidden = true;
        }
      });
      setDiff(windowed);
    } else {
      setDiff(paired);
    }
  }, [left, right, context, inline]);

  useEffect(() => { runDiff(); }, [left, right, context]);

  const loadSample = () => { setLeft(SAMPLE_A); setRight(SAMPLE_B); };

  const lineNumWidth = 38;

  return (
    <div>
      {/* toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:5 }}>
          <IconBtn onClick={loadSample}>Load Sample</IconBtn>
          <IconBtn
            onClick={()=>{ setLeft(''); setRight(''); setDiff(null); setStats(null); }}
            danger
          >
            Clear
          </IconBtn>
        </div>
        <div style={{ display:'flex', gap:4, alignItems:'center', marginLeft:'auto' }}>
          <span style={{ fontSize:11.5, color:T.muted }}>Context</span>
          {[3, 5, -1].map(n => (
            <Pill key={n} active={context===n} onClick={()=>setContext(n)} color={T.cyan}>
              {n === -1 ? 'Full' : `±${n}`}
            </Pill>
          ))}
        </div>
      </div>

      {/* stats bar */}
      {stats && (stats.added > 0 || stats.removed > 0) && (
        <div style={{
          display:'flex', gap:10, alignItems:'center',
          marginBottom:12, padding:'8px 12px',
          background:T.surface, borderRadius:7,
          border:`1px solid ${T.border}`,
          flexWrap:'wrap',
        }}>
          <span style={{ fontSize:11.5, color:T.dim, fontFamily:T.sans }}>Diff summary:</span>
          <span style={{ fontSize:12, color:T.green, fontFamily:T.mono, fontWeight:600 }}>
            +{stats.added} added
          </span>
          <span style={{ fontSize:12, color:T.red, fontFamily:T.mono, fontWeight:600 }}>
            −{stats.removed} removed
          </span>
          <span style={{ fontSize:12, color:T.muted, fontFamily:T.mono }}>
            {stats.same} unchanged
          </span>
          {stats.added === 0 && stats.removed === 0 && (
            <StatusBadge ok={true} label="Identical"/>
          )}
          {/* visual bar */}
          {stats.added + stats.removed > 0 && (
            <div style={{
              flex:1, minWidth:80, height:5, borderRadius:3,
              background:T.border, overflow:'hidden', marginLeft:4,
            }}>
              <div style={{
                height:'100%',
                width:`${(stats.added/(stats.added+stats.removed))*100}%`,
                background:`linear-gradient(90deg,${T.green},${T.green}80)`,
                borderRadius:3,
              }}/>
            </div>
          )}
        </div>
      )}

      {/* editors row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
        {[
          { val:left,  set:setLeft,  label:'Original', placeholder:'Paste original text here…' },
          { val:right, set:setRight, label:'Modified',  placeholder:'Paste modified text here…' },
        ].map(({ val, set, label, placeholder }) => (
          <div key={label}>
            <Label>{label}</Label>
            <textarea
              value={val}
              onChange={e => set(e.target.value)}
              placeholder={placeholder}
              spellCheck={false}
              style={{ ...EDITOR_STYLE, minHeight:160, resize:'vertical' }}
            />
          </div>
        ))}
      </div>

      {/* diff output */}
      {diff && diff.length > 0 && (
        <div>
          <Label>Diff</Label>
          <div style={{
            border:`1px solid ${T.border}`,
            borderRadius:8,
            overflow:'auto',
            background:T.bg,
            maxHeight:520,
          }}>
            {diff.map((item, i) => (
              item.type === 'ellipsis'
                ? (
                  <div key={i} style={{
                    padding:'3px 16px',
                    fontSize:11, fontFamily:T.mono,
                    color:T.dim, background:T.bg2,
                    borderTop:`1px solid ${T.border}`,
                    borderBottom:`1px solid ${T.border}`,
                    userSelect:'none',
                  }}>
                    ⋯ unchanged lines hidden
                  </div>
                )
                : (
                  <DiffLine
                    key={i}
                    item={item}
                    showInline={inline}
                    lineNumWidth={lineNumWidth}
                  />
                )
            ))}
          </div>
        </div>
      )}

      {/* empty state */}
      {(!diff || diff.length === 0) && (left || right) && stats?.added === 0 && stats?.removed === 0 && (
        <div style={{
          padding:'32px', textAlign:'center',
          border:`1px dashed ${T.border}`, borderRadius:8,
        }}>
          <div style={{ fontSize:28, marginBottom:8 }}>✓</div>
          <div style={{ fontSize:14, fontWeight:600, color:T.green, marginBottom:4 }}>
            Files are identical
          </div>
          <div style={{ fontSize:12, color:T.dim }}>No differences found</div>
        </div>
      )}

      {!left && !right && (
        <div style={{
          padding:'32px', textAlign:'center',
          border:`1px dashed ${T.border}`, borderRadius:8,
        }}>
          <div style={{ fontSize:11.5, color:T.dim, lineHeight:1.8 }}>
            Paste text into both boxes above to see the diff<br/>
            Supports code, JSON, plain text, config files, anything
          </div>
        </div>
      )}

      <div style={{ marginTop:10, fontSize:11, color:T.dim }}>
        Tip: Diff updates live as you type · Context controls how many unchanged lines are shown around changes
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────────────────────────────── */

// Add new tools here as you import them from DevToolsExtension.jsx
// e.g. import { YAMLFormatter, Base64Tool, ... } from './DevToolsExtension';
const TOOLS = [
  { id:'json',   label:'JSON Formatter',       icon:'{ }', subtitle:'Format · Validate · Minify', component: JSONTool  },
  { id:'diff',   label:'Text Diff',            icon:'±',   subtitle:'Compare any two texts',      component: DiffTool  },
  // Add new tools below — they auto-appear in the sidebar
  { id:'yaml',   label:'YAML Formatter',    icon:'~',   subtitle:'Format YAML',                component: YAMLFormatter  },
  { id:'base64', label:'Base64',            icon:'b64', subtitle:'Encode / Decode',            component: Base64Tool     },
  { id:'url',    label:'URL Encoder',       icon:'🔗',  subtitle:'Encode / Decode',            component: URLTool        },
  { id:'jwt',    label:'JWT Decoder',       icon:'🔑',  subtitle:'Decode & inspect',           component: JWTDecoder     },
  { id:'uuid',   label:'UUID Generator',    icon:'#',   subtitle:'v4 random UUIDs',            component: UUIDGenerator  },
  { id:'hash',   label:'Hash Generator',    icon:'##',  subtitle:'SHA-256 · SHA-512',          component: HashGenerator  },
  { id:'hmac',   label:'HMAC Generator',    icon:'🔐',  subtitle:'HMAC-SHA256',                component: HMACGenerator  },
  { id:'regex',  label:'Regex Tester',      icon:'.*',  subtitle:'Test & match',              component: RegexTester    },
];


const devToolsSchema = {
  "@context":    "https://schema.org",
  "@type":       "WebApplication",
  "name":        "Free Developer Tools — Tylented",
  "url":         "https://arhat.info/tools/dev",
  "description": "Free browser-based developer utilities: JSON formatter and validator, text diff, YAML formatter, Base64 encoder/decoder, URL encoder/decoder, JWT decoder, UUID v4 generator, SHA-256/384/512 hash generator, HMAC generator, regex tester with live highlighting.",
  "applicationCategory": "DeveloperApplication",
  "operatingSystem": "Any",
  "isAccessibleForFree": true,
  "offers": { "@type":"Offer", "price":"0", "priceCurrency":"USD" },
  "featureList": [
    "JSON Formatter, Validator and Minifier",
    "Text Diff Checker — compare two texts side by side",
    "YAML Formatter",
    "Base64 Encode and Decode",
    "URL Encoder and Decoder",
    "JWT Token Decoder — shows expiry and payload",
    "UUID v4 Generator — generate up to 50 at once",
    "Hash Generator — SHA-1, SHA-256, SHA-384, SHA-512",
    "HMAC Generator — HMAC-SHA256, HMAC-SHA512",
    "Regex Tester — live match highlighting and replace"
  ],
  "creator": { "@type":"Organization","name":"Tylented","url":"https://arhat.info" }
};
 
const devToolsFAQSchema = {
  "@context": "https://schema.org",
  "@type":    "FAQPage",
  "mainEntity": [
    {
      "@type":          "Question",
      "name":           "Is it safe to paste private data into the JSON formatter?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Yes — all processing at arhat.info/tools/dev happens entirely in your browser. No data is ever sent to a server. You can safely format sensitive API responses, configuration files, or private JSON data."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I decode a JWT token to see what's inside?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Paste your JWT token into the JWT Decoder at arhat.info/tools/dev. It instantly shows the decoded header and payload in readable JSON, and tells you if the token is expired based on the exp claim. Everything runs in your browser — the token is never sent anywhere."
      }
    },
    {
      "@type":          "Question",
      "name":           "How do I generate a SHA-256 hash online?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text":  "Type or paste text into the Hash Generator at arhat.info/tools/dev. Choose SHA-1, SHA-256, SHA-384, or SHA-512. The hash is generated instantly using your browser's built-in Web Crypto API — no server, no network request."
      }
    }
  ]
};
const Mapping = {
  'json': 'json',
  'json-formatter':     'json',
  'text-diff':  'diff',
  'yaml':         'yaml',
  'yaml-formatter':         'yaml',
  'base64':       'base64',
  'base64-encode':       'base64',
  'base64-decode':       'base64',
  'url':          'url',
  'url-encode':          'url',
  'url-decode':          'url',
  'jwt':          'jwt',

  'uuid':         'uuid',
  'hash':         'hash',
  'hmac':        'hmac',
  'regex':       'regex',
};

const DEV_FAQS = {
  json: [
    {
      q: 'What is a JSON formatter?',
      a: 'A JSON formatter (also called a JSON beautifier) takes compact or minified JSON and adds indentation and line breaks to make it human-readable. It also validates the JSON syntax and highlights any errors.',
    },
    {
      q: 'What is the difference between JSON formatting and JSON validation?',
      a: 'Formatting adds whitespace and indentation for readability. Validation checks that the JSON follows the correct syntax — properly closed brackets, quoted keys, no trailing commas, and valid value types. This tool does both simultaneously.',
    },
    {
      q: 'Why does my JSON fail to parse?',
      a: 'Common causes: trailing commas after the last item in an array or object (not allowed in JSON), single quotes instead of double quotes, unquoted keys, comments (not supported in JSON), or a missing closing bracket or brace.',
    },
    {
      q: 'What is the difference between JSON and JSONC?',
      a: 'JSON does not support comments. JSONC (JSON with Comments) is a superset used by tools like VS Code\'s settings files that allows // and /* */ comments. Standard JSON parsers will reject JSONC — strip comments before parsing.',
    },
    {
      q: 'Does my JSON data leave my browser?',
      a: 'No — all JSON formatting and validation happens entirely in your browser using JavaScript. Nothing is sent to any server. This makes it safe to use with sensitive API responses or configuration files.',
    },
  ],

  diff: [
    {
      q: 'What is a text diff tool?',
      a: 'A diff tool compares two pieces of text and highlights the differences between them — additions, deletions, and changes. It\'s used to review code changes, compare document versions, or spot differences between two JSON or config files.',
    },
    {
      q: 'What algorithm does the diff tool use?',
      a: 'Most diff tools use the Myers diff algorithm or a variation of it, which finds the shortest edit script (minimum number of additions and deletions) to transform one text into the other. This produces the most readable and minimal diff output.',
    },
    {
      q: 'What is a line-level vs character-level diff?',
      a: 'Line-level diff shows which entire lines were added or removed. Character-level diff highlights the specific characters that changed within a line. Character-level is more precise for spotting small changes like a single digit or letter.',
    },
    {
      q: 'Can I use this to compare JSON files?',
      a: 'Yes — paste both JSON objects and the diff will show exactly which fields changed. For the clearest results, format both JSONs first (using the JSON Formatter tab) so they have consistent indentation before comparing.',
    },
  ],

  yaml: [
    {
      q: 'What is YAML?',
      a: 'YAML (YAML Ain\'t Markup Language) is a human-readable data serialisation format commonly used for configuration files. Docker Compose, Kubernetes, GitHub Actions, and many other tools use YAML for their config.',
    },
    {
      q: 'What is the difference between YAML and JSON?',
      a: 'YAML uses indentation for structure and is more readable for humans. JSON uses braces and brackets and is more explicit. YAML supports comments; JSON does not. YAML is a superset of JSON — valid JSON is valid YAML.',
    },
    {
      q: 'Why does my YAML fail to parse?',
      a: 'Common causes: inconsistent indentation (mixing tabs and spaces — YAML requires spaces only), missing quotes around special characters like colons in values, incorrect list formatting, or duplicate keys.',
    },
    {
      q: 'What does YAML to JSON conversion do?',
      a: 'It parses your YAML structure and outputs the equivalent JSON. This is useful when an API requires JSON input but your config is written in YAML, or when you want to validate YAML by viewing its parsed structure.',
    },
  ],

  base64: [
    {
      q: 'What is Base64 encoding?',
      a: 'Base64 is an encoding scheme that converts binary data into a text string using 64 printable ASCII characters. It\'s used to safely transmit binary data (like images or files) over systems that only handle text, such as email or JSON APIs.',
    },
    {
      q: 'Is Base64 the same as encryption?',
      a: 'No — Base64 is encoding, not encryption. Anyone can decode a Base64 string instantly without a key. It is not a security measure. Use it for data transport, not for hiding sensitive information.',
    },
    {
      q: 'What is the difference between standard Base64 and URL-safe Base64?',
      a: 'Standard Base64 uses + and / characters which have special meaning in URLs. URL-safe Base64 replaces + with - and / with _ so the encoded string can be used directly in URLs and query parameters without percent-encoding.',
    },
    {
      q: 'Why does my Base64 string end with == ?',
      a: 'Base64 encodes 3 bytes at a time into 4 characters. If the input length isn\'t a multiple of 3, padding characters (=) are added to make the output length a multiple of 4. One = means 1 byte of padding; == means 2 bytes.',
    },
    {
      q: 'Does my data stay in the browser?',
      a: 'Yes — all Base64 encoding and decoding is done entirely in JavaScript in your browser. No data is transmitted to any server.',
    },
  ],

  url: [
    {
      q: 'What is URL encoding?',
      a: 'URL encoding (also called percent-encoding) converts special characters into a % followed by two hex digits so they can be safely included in a URL. For example, a space becomes %20 and & becomes %26.',
    },
    {
      q: 'When do I need to URL encode something?',
      a: 'Any time you include user input or special characters in a URL — query parameters, path segments, form submissions. Characters like &, =, ?, #, and spaces have special meaning in URLs and must be encoded when used as data.',
    },
    {
      q: 'What is the difference between encodeURI and encodeURIComponent?',
      a: 'encodeURI encodes a full URL and leaves characters like /, ?, &, = untouched. encodeURIComponent encodes a URL component (like a single query parameter value) and encodes those characters too. Use encodeURIComponent for individual values.',
    },
    {
      q: 'What is a query string?',
      a: 'The query string is the part of a URL after the ? character. It contains key=value pairs separated by &. For example: ?name=John&age=30. Values in query strings must be URL encoded to handle special characters.',
    },
  ],

  jwt: [
    {
      q: 'What is a JWT?',
      a: 'A JWT (JSON Web Token) is a compact, URL-safe token format used for authentication and information exchange. It consists of three Base64URL-encoded parts separated by dots: header, payload, and signature.',
    },
    {
      q: 'What information is inside a JWT?',
      a: 'The header contains the token type and signing algorithm. The payload contains claims — data like user ID, email, roles, expiry time (exp), issued-at time (iat), and issuer (iss). The signature verifies the token hasn\'t been tampered with.',
    },
    {
      q: 'Is it safe to decode a JWT in the browser?',
      a: 'Yes — decoding reads the header and payload which are just Base64URL-encoded JSON. They are not encrypted. However, never paste production tokens into online tools you don\'t control. This tool processes everything locally.',
    },
    {
      q: 'How do I verify a JWT signature?',
      a: 'Signature verification requires the secret key (for HMAC algorithms) or the public key (for RSA/ECDSA). The server verifies signatures; this tool decodes the payload for inspection only. Always verify signatures server-side.',
    },
    {
      q: 'What does "token expired" mean?',
      a: 'The exp (expiration) claim in the JWT payload is a Unix timestamp. If the current time is past that timestamp the token is expired and should be rejected. This tool shows whether your token is expired based on the exp field.',
    },
  ],

  uuid: [
    {
      q: 'What is a UUID?',
      a: 'A UUID (Universally Unique Identifier) is a 128-bit number formatted as 32 hexadecimal digits in groups of 8-4-4-4-12, like 550e8400-e29b-41d4-a716-446655440000. They\'re used as unique IDs in databases, APIs, and distributed systems.',
    },
    {
      q: 'What is the difference between UUID v4 and UUID v1?',
      a: 'UUID v4 is randomly generated — it has no meaningful content and is the most common type for IDs. UUID v1 includes the current timestamp and MAC address, which can reveal when and where it was generated. Use v4 for most purposes.',
    },
    {
      q: 'Are UUIDs truly unique?',
      a: 'UUID v4 has 122 bits of randomness, giving 2^122 possible values. The probability of two UUIDs colliding is astronomically small — roughly 1 in 5.3 × 10^36. For practical purposes they are unique.',
    },
    {
      q: 'Should I use UUIDs or auto-increment integers as database IDs?',
      a: 'UUIDs work better for distributed systems, APIs where you don\'t want to expose sequential IDs, and merging data from multiple databases. Auto-increment integers are simpler, smaller, and faster to index in a single database.',
    },
  ],

  hash: [
    {
      q: 'What is a hash function?',
      a: 'A hash function takes any input and produces a fixed-length output (the hash or digest). The same input always produces the same hash, but even a tiny input change produces a completely different hash. Hashes are one-way — you cannot reverse them to get the original input.',
    },
    {
      q: 'What is the difference between SHA-256, SHA-384, and SHA-512?',
      a: 'They are all part of the SHA-2 family. The number refers to the output size in bits: SHA-256 produces 64 hex characters, SHA-384 produces 96, SHA-512 produces 128. Longer hashes are harder to brute force but slightly slower to compute.',
    },
    {
      q: 'Can I use SHA-256 to store passwords?',
      a: 'No — SHA-256 is too fast, making it vulnerable to brute-force attacks. For passwords use bcrypt, scrypt, or Argon2 which are specifically designed to be slow and resistant to GPU cracking. SHA-256 is for data integrity, not password hashing.',
    },
    {
      q: 'What is a hash collision?',
      a: 'A collision occurs when two different inputs produce the same hash output. SHA-256 has no known practical collisions. MD5 and SHA-1 have known collisions and should not be used for security purposes.',
    },
    {
      q: 'Does the input leave my browser?',
      a: 'No — hashing uses the browser\'s built-in Web Crypto API and runs entirely locally. Nothing is sent to any server. This makes it safe to hash sensitive data like passwords or API keys for comparison purposes.',
    },
  ],

  hmac: [
    {
      q: 'What is HMAC?',
      a: 'HMAC (Hash-based Message Authentication Code) combines a secret key with a hash function to produce a signature. Unlike a plain hash, HMAC can only be verified by someone who has the secret key, making it useful for verifying message authenticity.',
    },
    {
      q: 'What is the difference between HMAC and a regular hash?',
      a: 'A regular hash has no secret — anyone can compute it. An HMAC requires a secret key, so only parties who share the key can generate or verify it. HMAC proves both that the message is intact and that the sender knew the secret key.',
    },
    {
      q: 'What is HMAC used for in practice?',
      a: 'Webhook signature verification (Stripe, GitHub, Shopify all sign webhook payloads with HMAC), API request signing (AWS Signature v4 uses HMAC-SHA256), JWT signing with HS256, and session tokens.',
    },
    {
      q: 'How do I verify a Stripe webhook signature?',
      a: 'Stripe signs the raw request body with your webhook secret using HMAC-SHA256 and includes the result in the Stripe-Signature header. Compute the HMAC of the raw body using your secret and compare it to the header value.',
    },
  ],

  regex: [
    {
      q: 'What is a regular expression (regex)?',
      a: 'A regular expression is a pattern that describes a set of strings. It\'s used to search, match, and extract text. Regex patterns can match simple strings like "hello" or complex patterns like email addresses or phone numbers.',
    },
    {
      q: 'What are the most useful regex flags?',
      a: 'g (global) finds all matches, not just the first. i (case-insensitive) makes matching ignore case. m (multiline) makes ^ and $ match start/end of each line. s (dotAll) makes . match newlines. These can be combined: /pattern/gi.',
    },
    {
      q: 'What is the difference between .* and .+?',
      a: '.* matches zero or more of any character. .+ matches one or more. The difference matters when the input could be empty — .* will match an empty string while .+ requires at least one character.',
    },
    {
      q: 'What is a capture group?',
      a: 'Parentheses create a capture group: (\\d+). Whatever matches inside is captured and can be extracted separately. Named groups use (?<name>\\d+) syntax. Non-capturing groups use (?:\\d+) when you need grouping without capturing.',
    },
    {
      q: 'Why does my regex work in one language but not another?',
      a: 'Regex flavours differ between languages. JavaScript doesn\'t support lookbehinds in older engines, Python uses re.VERBOSE for comments, PCRE (PHP/Perl) supports more features than POSIX (grep/sed). Always test in the target language.',
    },
  ],
};

const GENERIC_DEV_FAQS = [
  {
    q: 'Does any data leave my browser when I use these tools?',
    a: 'No — all developer tools (JSON formatter, Base64, JWT decoder, hash generator, regex tester, etc.) run entirely in your browser using JavaScript and the Web Crypto API. Nothing is sent to any server.',
  },
  {
    q: 'Are these tools free?',
    a: 'Yes — all developer utilities are completely free with no account required.',
  },
  {
    q: 'Can I use these tools on mobile?',
    a: 'Yes — all tools are mobile-responsive. The sidebar collapses to a dropdown on small screens.',
  },
];

function DevFAQSection({ active }) {
  const [open, setOpen] = useState(null);
  const faqs = DEV_FAQS[active] || GENERIC_DEV_FAQS;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {faqs.map((faq, i) => {
        const isOpen = open === i;
        return (
          <div key={i} style={{
            border:     `1px solid ${isOpen ? T.blue+'55' : T.border}`,
            borderRadius:10,
            background:  isOpen ? T.blue+'08' : T.bg2,
            overflow:   'hidden',
            transition: 'all .2s',
          }}>
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              style={{
                width:'100%', display:'flex', justifyContent:'space-between',
                alignItems:'center', padding:'13px 16px',
                background:'transparent', border:'none',
                cursor:'pointer', textAlign:'left', gap:12,
              }}
            >
              <span style={{
                fontSize:13.5, fontWeight:600,
                color: isOpen ? T.blue : T.white,
                lineHeight:1.4, transition:'color .15s',
              }}>
                {faq.q}
              </span>
              <span style={{
                flexShrink:0, width:20, height:20, borderRadius:'50%',
                border:`1px solid ${isOpen ? T.blue : T.border}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:13, color: isOpen ? T.blue : T.muted,
                transition:'all .2s',
                transform: isOpen ? 'rotate(45deg)' : 'none',
              }}>+</span>
            </button>

            {isOpen && (
              <div style={{
                padding:'0 16px 14px', paddingTop:12,
                fontSize:13, color:'rgba(220,230,250,.65)',
                lineHeight:1.75,
                borderTop:`1px solid ${T.blue}20`,
              }}>
                {faq.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function DevTools() {
        const navigate = useNavigate();
      
        const hostname = window.location.hostname;
      
      // app.dev.example.com => ["app", "dev", "example", "com"]
      const parts = hostname.split(".");
      
      const firstSubdomain =
        parts.length > 2 ? parts[0] : null;
      
      console.log(firstSubdomain);
      
      const path = window.location.pathname;
      
      // "/tools/useragent/"
      const match = path.match(/^\/tools\/dev\/([^/]+)/);
      
      const toolName = match ? match[1] : null;          
          
        const [active, setActive] = useState(toolName?toolName:firstSubdomain && firstSubdomain in Mapping ? Mapping[firstSubdomain] : 'json');
            
      useEffect(() => {
  // Pass the two schemas for this page
  const schemas = [devToolsSchema, devToolsFAQSchema];
  const ids     = [];
  schemas.forEach((schema, i) => {
    const id  = `ld-json-tool-${i}`;
    document.getElementById(id)?.remove();
    const s   = document.createElement('script');
    s.id      = id;
    s.type    = 'application/ld+json';
    s.text    = JSON.stringify(schema);
    document.head.appendChild(s);
    ids.push(id);
  });
  return () => ids.forEach(id => document.getElementById(id)?.remove());
}, []);
      console.log(toolName);
      
        
  
  const meta = TOOLS.find(t => t.id === active);
  const Tool = meta?.component;

  return (
    <>
    
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        ::placeholder { color:rgba(255,255,255,0.50) !important; }
        textarea:focus, input:focus, select:focus {
          border-color:rgba(77,159,255,.4) !important; outline:none;
        }
        textarea { transition:border-color .15s; }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,.1); border-radius:3px; }
        ::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,.18); }
        .dt-layout  { display:grid; grid-template-columns:200px 1fr; }
        .dt-sidebar { display:block; }
        .dt-mobile  { display:none; }
        .dt-two-col { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        @media (max-width:700px) {
          .dt-layout  { grid-template-columns:1fr !important; }
          .dt-sidebar { display:none !important; }
          .dt-mobile  { display:block !important; margin-bottom:12px; }
          .dt-two-col { grid-template-columns:1fr !important; }
        }
            /* ADD THESE — reset all inputs and buttons globally */
  input, select, textarea {
    box-sizing: border-box;
    font-family: ${T.mono};
    background: ${T.bg};
    color: ${T.white};
    border: 1px solid ${T.border};
    border-radius: 7px;
    outline: none;
    transition: border-color .15s;
  }
  input:focus, select:focus, textarea:focus {
    border-color: rgba(77,159,255,.4) !important;
  }
  button {
    font-family: ${T.sans};
    cursor: pointer;
  }
  select option {
    background: ${T.bg2};
    color: ${T.white};
  }
  ::placeholder {
    color: ${T.dim} !important;
  }
/* Extension tool elements */
.dte-input {
  width: 100%;
  padding: 9px 12px;
  background: #0a0c10 !important;
  border: 1px solid rgba(255,255,255,.07) !important;
  border-radius: 7px;
  color: #e8edf5 !important;
  font-size: 13px;
  font-family: "JetBrains Mono","Fira Code",monospace;
  outline: none;
  box-sizing: border-box;
  display: block;
  transition: border-color .15s;
}
.dte-textarea {
  width: 100%;
  min-height: 160px;
  padding: 14px 16px;
  background: #0a0c10 !important;
  border: 1px solid rgba(255,255,255,.07) !important;
  border-radius: 8px;
  color: #e8edf5 !important;
  font-size: 12.5px;
  font-family: "JetBrains Mono","Fira Code",monospace;
  line-height: 1.65;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  display: block;
  transition: border-color .15s;
}
.dte-textarea-out {
  width: 100%;
  min-height: 160px;
  padding: 14px 16px;
  background: #13171f !important;
  border: 1px solid rgba(255,255,255,.07) !important;
  border-radius: 8px;
  color: #e8edf5 !important;
  font-size: 12.5px;
  font-family: "JetBrains Mono","Fira Code",monospace;
  line-height: 1.65;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  display: block;
}
.dte-btn {
  padding: 6px 16px;
  border-radius: 6px;
  border: 1px solid rgba(77,159,255,.5) !important;
  background: rgba(77,159,255,.15) !important;
  color: #4d9fff !important;
  font-size: 12.5px;
  font-weight: 600;
  font-family: "DM Sans","Outfit",system-ui,sans-serif;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all .15s;
}
.dte-btn:hover {
  background: rgba(77,159,255,.25) !important;
  border-color: rgba(77,159,255,.7) !important;
}
.dte-btn-cyan {
  border-color: rgba(0,212,255,.5) !important;
  background: rgba(0,212,255,.15) !important;
  color: #00d4ff !important;
}
.dte-btn-cyan:hover {
  background: rgba(0,212,255,.25) !important;
}
      `}</style>
 <div className="page">

    <section className="section" style={{ paddingTop: 0 }}>
      <SEO
  title="JSON Formatter, Base64, JWT Decoder & Developer Tools — Free | Tylented"
  description="Free browser-based developer utilities: format and validate JSON, compare text differences, encode/decode Base64 and URLs, decode JWT tokens, generate UUIDs, create SHA-256/512 hashes, generate HMAC signatures, test regex patterns. Your data never leaves your browser."
  keywords="json formatter online free, json validator, json beautifier, text diff checker, compare two texts online, base64 encode decode, url encoder decoder, jwt decoder online, uuid generator free, sha256 hash generator, hmac generator online, regex tester online, yaml formatter, developer tools free browser"
  canonical="https://arhat.info/tools/dev"
  og={{
    title:       "Free JSON Formatter, JWT Decoder & Developer Utilities — Tylented",
    description: "13 free browser-based tools: JSON formatter/validator, text diff, YAML, Base64, URL encoder, JWT decoder, UUID generator, hash generator, regex tester. 100% private.",
    url:         "https://arhat.info/tools/dev",
    image:       "https://arhat.info/og-tools-dev.png",
  }}
/>
      <div style={{ minHeight:'100vh', background:T.bg, padding:'28px 0 48px', fontFamily:T.sans }}>

        {/* Header */}
        <div style={{ marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:5 }}>
            <div style={{
              width:32, height:32, borderRadius:8, fontSize:15,
              background:`${T.blue}20`, border:`1px solid ${T.blue}30`,
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>⌨</div>
            <h1 style={{ fontSize:'clamp(1.4rem,3vw,1.9rem)', fontWeight:700, color:T.white, letterSpacing:'-.02em', fontFamily:T.sans }}>
              Developer Tools
            </h1>
          </div>
          <p style={{ fontSize:13, color:T.muted, marginLeft:42 }}>
            JSON, diff, regex, JWT, UUID, hash — all in your browser, nothing sent to any server
          </p>
        </div>

        {/* Mobile dropdown — hidden on desktop */}
        <div className="dt-mobile">
          <select
            value={active}
            onChange={e => setActive(e.target.value)}
            onChange={(e) => {navigate(`/tools/dev/${e.target.value}/`)
            setActive(e.target.value);
          }}
            style={{
              width:'100%', padding:'10px 12px', borderRadius:8,
              background:T.surface, border:`1px solid ${T.borderHi}`,
              color:T.white, fontSize:13, fontFamily:T.sans, cursor:'pointer',
            }}
          >
            {TOOLS.map(t => (
              <option key={t.id} value={t.id}>{t.icon}  {t.label}</option>
            ))}
          </select>
        </div>

        {/* Sidebar + panel */}
        <div className="dt-layout" style={{
          border:`1px solid ${T.border}`, borderRadius:12,
          overflow:'hidden', minHeight:520,
        }}>

          {/* Sidebar */}
          <div className="dt-sidebar" style={{
            borderRight:`1px solid ${T.border}`,
            background:T.bg2, padding:'12px 0', overflowY:'auto',
          }}>
            {TOOLS.map(t => (
              <button key={t.id} onClick={() => {
                 navigate(`/tools/dev/${t.id}/`)
                setActive(t.id)}
              } 
                
                style={{
                width:'100%', padding:'10px 16px', border:'none',
                borderLeft:`2px solid ${active===t.id ? T.blue : 'transparent'}`,
                background: active===t.id ? T.blue+'10' : 'transparent',
                color: active===t.id ? T.white : T.muted,
                fontSize:12.5, fontFamily:T.sans, cursor:'pointer',
                textAlign:'left', transition:'all .13s',
                display:'flex', alignItems:'center', gap:10,
              }}>
                <span style={{
                  width:24, height:24, borderRadius:5, flexShrink:0,
                  background: active===t.id ? T.blue+'20' : T.surface,
                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                  fontSize:10.5, fontFamily:T.mono, fontWeight:700,
                  color: active===t.id ? T.blue : T.dim,
                }}>{t.icon}</span>
                <div>
                  <div style={{ fontWeight: active===t.id ? 600 : 400 }}>{t.label}</div>
                  <div style={{ fontSize:10.5, color:T.dim, marginTop:1 }}>{t.subtitle}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Tool panel */}
          <div style={{ padding:'22px 24px', background:T.bg3, overflowY:'auto' }}>
            <div style={{ marginBottom:18 }}>
              <h2 style={{ fontSize:16, fontWeight:700, color:T.white, marginBottom:3, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{
                  width:26, height:26, borderRadius:6,
                  background:T.blue+'20', border:`1px solid ${T.blue}30`,
                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                  fontSize:11, fontFamily:T.mono, fontWeight:700, color:T.blue,
                }}>{meta?.icon}</span>
                {meta?.label}
              </h2>
              <p style={{ fontSize:12, color:T.dim, marginLeft:34 }}>{meta?.subtitle}</p>
            </div>
             {active === 'json' && <JSONTool/>}
                      {active === 'diff' && <DiffTool/>}
                      {active === 'yaml' && <YAMLFormatter/>}
                      {active === 'base64' && <Base64Tool/>}
                      {active === 'url' && <URLTool/>}
                      {active === 'jwt' && <JWTDecoder/>}
                      {active === 'jwtgen' && <JWTGenerator/>}
                      {active === 'uuid' && <UUIDGenerator/>}
                      {active === 'hash' && <HashGenerator/>}
                      {active === 'hmac' && <HMACGenerator/>}
                      {active === 'regex' && <RegexTester/>}
          </div>
        </div>

        <div style={{ marginTop:16, textAlign:'center', fontSize:11.5, color:T.dim }}>
          🔒 All processing happens locally in your browser — no data is sent to any server
        </div>
        <div style={{ marginTop:48 }}>
  <h2 style={{
    fontSize:18, fontWeight:800, color:T.white,
    letterSpacing:'-.02em', marginBottom:6,
  }}>
    Frequently Asked Questions
  </h2>
  <p style={{ fontSize:13, color:T.muted, marginBottom:24 }}>
    Common questions about developer utilities and browser-based tools.
  </p>
  <DevFAQSection active={active}/>
</div>

      </div></section></div>
    </>
  );
}